declare global {
  interface Window {
    dataLayer: DataLayerEvent[];
  }
}

export type AnalyticsEvent =
  | 'funnelStepLoad'
  | 'promoAttempt'
  | 'errorMessage'
  | 'emailQuote'
  | 'emailQuoteConfirmation'
  | 'callbackConfirmation'
  | 'clicModifVelo'
  | 'purchase';

export interface DataLayerEvent {
  event?: string;
  action?: string;
  libelle?: string;
  categorie?: string;
  valeur?: string;
  environnement?: string;
  [key: string]: unknown;
}

export interface FunnelStepLoadData {
  stepName: string;
  parentStepName?: string;
  currentInfo: Record<string, unknown>;
}

export interface PromoAttemptData {
  promoCode: string;
  status: 'Réussite' | 'Echec';
}

export interface ErrorMessageData {
  label: string;
}

export interface AnalyticsEventPayload {
  event: AnalyticsEvent;
  ap1?: string | number;
  datas?: Record<string, unknown>;
}

const AMO_APPORTEUR = '43397';

/**
 * Initialise le dataLayer avec l'environnement
 */
export const initDataLayer = (environment: string) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ environnement: environment });
};

/**
 * Push un événement dans le dataLayer (GTM)
 * Note: Les événements sont uniquement envoyés pour l'apporteur AMO (43397)
 */
export const pushAnalyticsEvent = ({ event, ap1, datas }: AnalyticsEventPayload) => {
  window.dataLayer = window.dataLayer || [];

  // Uniquement pour AMO
  if (ap1 && String(ap1) === AMO_APPORTEUR) {
    if (datas) {
      window.dataLayer.push({ event, ...datas });
    } else {
      window.dataLayer.push({ event });
    }
  }
};

/**
 * Push un événement générique (sans filtre apporteur)
 */
export const pushEvent = (payload: DataLayerEvent) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

/**
 * Événement de chargement d'étape du funnel
 */
export const trackFunnelStep = (
  stepName: string,
  currentInfo: Record<string, unknown>,
  ap1?: string | number
) => {
  pushAnalyticsEvent({
    event: 'funnelStepLoad',
    ap1,
    datas: {
      funnelStepLoad: {
        stepName,
        parentStepName: '',
        currentInfo,
      },
    },
  });
};

/**
 * Événement de tentative de code promo
 */
export const trackPromoAttempt = (
  promoCode: string,
  eligible: boolean,
  ap1?: string | number
) => {
  pushAnalyticsEvent({
    event: 'promoAttempt',
    ap1,
    datas: {
      promoAttempt: {
        promoCode,
        status: eligible ? 'Réussite' : 'Echec',
      },
    },
  });
};

/**
 * Événement d'erreur
 */
export const trackError = (label: string, ap1?: string | number) => {
  pushAnalyticsEvent({
    event: 'errorMessage',
    ap1,
    datas: {
      errorMessage: { label },
    },
  });
};

/**
 * Événement d'envoi de devis par email
 */
export const trackEmailQuote = (ap1?: string | number) => {
  pushAnalyticsEvent({ event: 'emailQuote', ap1 });
};

/**
 * Événement de confirmation d'envoi de devis
 */
export const trackEmailQuoteConfirmation = (ap1?: string | number) => {
  pushAnalyticsEvent({ event: 'emailQuoteConfirmation', ap1 });
};

/**
 * Événement de confirmation de demande de rappel
 */
export const trackCallbackConfirmation = (ap1?: string | number) => {
  pushAnalyticsEvent({ event: 'callbackConfirmation', ap1 });
};

/**
 * Événement de clic sur modification (sidebar)
 */
export const trackModificationClick = (section: string, ap1?: string | number) => {
  pushAnalyticsEvent({
    event: 'clicModifVelo',
    ap1,
    datas: { section },
  });
};

// ============================================================================
// Formatage des données pour analytics
// ============================================================================

type FormType = 'velo' | 'trottinette';

const FieldsLabel: Record<FormType, Record<string, string>> = {
  velo: {
    'DemandeTarif[Vehicule][GenreVehicule]': 'vehicleType',
    'DemandeTarif[Vehicule][Designation]': 'brand',
    'DemandeTarif[Vehicule][ValeurVehicule]': 'vehiclePrice',
    'DemandeTarif[DevoirConseil][DevoirIC]': 'protection',
    yealyQuote: 'yearlyQuote',
    'DemandeContrat[VIN]': 'tagPresent',
    'DemandeContrat[DateHeureEffet]': 'startDate',
    numeroContrat: 'contractID',
    'DemandeContrat[TiersPayeur][Relationship]': 'link',
    'DemandeTarif[ListePersonnes][0][Email]': 'email',
  },
  trottinette: {
    'DemandeTarif[Police][FormuleChoisie]': 'concept',
    'DemandeTarif[ListeOptions][0][Souscrite]': 'invalidity',
    yealyQuote: 'yearlyQuote',
    numeroContrat: 'contractID',
    'DemandeContrat[TiersPayeur][Relationship]': 'link',
    'DemandeTarif[ListePersonnes][0][Email]': 'email',
  },
};

const vehiculeTypeVelo: Record<string, string> = {
  VEL: 'Vélo Classique',
  VAE: 'VAE',
  VELC: 'Cargo',
};

const formulesTrottinette: Record<string, string> = {
  F1: 'Solo',
  F2: 'Famille',
};

const RelationsTiersPayeur: Record<string, string> = {
  C: 'Conjoint',
  E: 'Enfant',
  FS: 'Frère/Sœur',
  GP: 'Grand-parent',
  P: 'Parent',
  A: 'Autres',
};

const getNestedValue = (obj: Record<string, unknown>, path: string): unknown => {
  const normalizedPath = path.replace(/\[([^\]]+)\]/g, '.$1');
  return normalizedPath.split('.').reduce((current: unknown, key) => {
    if (current && typeof current === 'object') {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
};

const formatValueForVelo = (label: string, value: unknown): string => {
  switch (label) {
    case 'vehicleType':
      return vehiculeTypeVelo[value as string] ?? String(value);
    case 'protection':
    case 'tagPresent':
      return String(value) === '1' || String(value) === 'true' || value === true
        ? 'oui'
        : 'non';
    case 'link':
      return RelationsTiersPayeur[value as string] ?? String(value);
    default:
      return String(value);
  }
};

const formatValueForTrottinette = (label: string, value: unknown): string => {
  switch (label) {
    case 'concept':
      return formulesTrottinette[value as string] ?? String(value);
    case 'invalidity':
      return String(value) === '1' || String(value) === 'true' ? 'oui' : 'non';
    case 'link':
      return RelationsTiersPayeur[value as string] ?? String(value);
    default:
      return String(value);
  }
};

/**
 * Formate les données du formulaire pour l'envoi analytics
 */
export const formatDataForAnalytics = (
  formType: FormType,
  data: Record<string, unknown>
): Record<string, string> => {
  const fields = FieldsLabel[formType];
  if (!fields) return {};

  const result: Record<string, string> = {};

  for (const [oldLabel, newLabel] of Object.entries(fields)) {
    const value = getNestedValue(data, oldLabel);

    if (value !== undefined && value !== null && value !== '') {
      let formattedValue: string;

      if (formType === 'velo') {
        formattedValue = formatValueForVelo(newLabel, value);
      } else {
        formattedValue = formatValueForTrottinette(newLabel, value);
      }

      if (formattedValue) {
        result[newLabel] = result[newLabel]
          ? `${result[newLabel]}, ${formattedValue}`
          : formattedValue;
      }
    }
  }

  return result;
};
